import React, {
  ChangeEvent,
  Fragment,
  useContext,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react"
import * as z from "zod"
import ConsultationSuccessPopup from "./ConsultationSuccessPopup"
import CustomDayPicker from "../date-time/CustomDayPicker"
import Loader from "../ui/loader"
import moment from "moment"
import Recaptcha from "../auth/register-form/Recaptcha"
import secureLocalStorage from "react-secure-storage"
import TimeOptions from "../scheduling/new-meeting/step-views/TimeOptions"
import { AvailabilityRulesData } from "../../types/availabilityTypes"
import { Button } from "../ui/button"
import { CalendarRequests, MeetingRequests } from "../../api/app.service"
import { GetAvailability } from "../../models/app.interface"
import { Input } from "../ui/input"
import { Link } from "react-router-dom"
import { RegisterFormContext } from "../auth/register-form/context"
import { SvgWarning } from "../icons"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { UserExists } from "../../api/auth.service"
import { useToast } from "../../hooks/useToast"

interface BookingState {
  start: Date
  end: Date
}

interface Props {
  coachPublicProfileData: any
  service: any
  getProfileServiceList: any
  coachColor: any
  canBookFreeConsultation?: any
}

const FreeConsultGuestCreation: React.FC<Props> = ({
  coachPublicProfileData,
  service,
  getProfileServiceList,
  coachColor,
  canBookFreeConsultation,
}) => {
  const [loadingAvailablePeriods, setLoadingAvailablePeriods] =
    useState<boolean>(false)
  const [activeDate, setActiveDate] = useState<Date>(new Date())
  const [bookingWindow, setBookingWindow] = useState<number>(0)
  const durationDefault = 60
  const localTimeZone = moment.tz.guess()
  const [availablePeriods, setAvailablePeriods] = useState<Array<any>>([])
  const [userAvailabilities, setUserAvailabilities] = useState<
    Array<AvailabilityRulesData>
  >([])
  const { toast } = useToast()
  const { user } = useContext(UserCtx)
  const [prepopulatedStartTime, setPrepopulatedStartTime] =
    useState<Date | null>(null)
  const [prepopulatedEndTime, setPrepopulatedEndTime] = useState<Date | null>(
    null
  )
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [UTCStartTime, setUTCStartTime] = useState<string | undefined>()
  const [UTCEndTime, setUTCEndTime] = useState<string | undefined>()
  const [isOwner, setIsOwner] = useState<boolean>()
  const [isNotRobot, setIsNotRobot] = useState<boolean>(false)
  const [rulesLoading, setRulesLoading] = useState(true)

  const leftContainerRef = useRef<HTMLDivElement | null>(null)
  const [leftHeight, setLeftHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (leftContainerRef.current) {
      setLeftHeight(leftContainerRef.current.offsetHeight)
    }
  }, [loadingAvailablePeriods])

  const location = useLocation()
  const navigate = useNavigate()
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const {
    email,
    setEmail,
    setFirstName,
    firstName,
    setLastName,
    lastName,
    createAccount,
    setPassword,
    setIsTemp,
  } = useContext(RegisterFormContext)

  const [errors, setErrors] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confUserPassword: "",
  })

  const generatePassword = (length: number = 12): string => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const lower = "abcdefghijklmnopqrstuvwxyz"
    const digits = "0123456789"
    const specials = "!@#$%^&*()_+-=[]{}|;:,.<>?"
    const all = upper + lower + digits + specials

    const getRandom = (str: string) =>
      str.charAt(Math.floor(Math.random() * str.length))

    let password = [
      getRandom(upper),
      getRandom(lower),
      getRandom(digits),
      getRandom(specials),
    ]

    for (let i = password.length; i < length; i++) {
      password.push(getRandom(all))
    }

    return password.sort(() => Math.random() - 0.5).join("")
  }

  const createMeeting = async () => {
    setLoading(true)
    const tempUserJSON = secureLocalStorage.getItem("user")
    const tempUser = tempUserJSON ? JSON.parse(tempUserJSON as string) : null
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    try {
      await MeetingRequests.createConsultationMeeting({
        product_id: service.stripe_product_id,
        coach_user_id: parseInt(coachPublicProfileData.user_id),
        profile_id: tempUser?.member_profile_id,
        prospect_details: {
          first_name: firstName,
          last_name: lastName,
          email: email,
          timezone: userTimezone,
          user_id: parseInt(tempUser?.user_id),
          profile_id: tempUser?.member_profile_id,
        },
        meeting_details: {
          name: service.service_details.title,
          meeting_type: "individual",
          start_time: UTCStartTime?.replace(/\.\d{3}Z$/, "Z") ?? "",
          end_time: UTCEndTime?.replace(/\.\d{3}Z$/, "Z") ?? "",
          medium: "zoee_video",
          status: "busy",
          reminders: [],
          coach_details: {
            profile_id: coachPublicProfileData.coach_profile_id,
            user_id: parseInt(coachPublicProfileData.user_id),
          },
        },
      })
      setShowSuccessPopup(true)
    } catch (ex: any) {
      toast.error(ex.response.data.message)
    } finally {
      secureLocalStorage.removeItem("user")
      setLoading(false)
    }
  }

  const duration = service?.service_details?.session?.duration
    ? parseInt(service?.service_details?.session?.duration) / 60
    : 60

  const submitForm = async () => {
    setLoading(true)

    const emailSchema = z.string().email().safeParse(email)
    if (!emailSchema.success) {
      setError(emailSchema.error.issues[0].message)
      setLoading(false)
      return
    }

    const result = await doesEmailExist(email)

    if (result?.found) {
      toast.error(
        "This email is already associated with an existing account. Please sign in."
      )
      setLoading(false)
      return
    }

    try {
      await createAccount()
      await createMeeting()
      setLoading(false)
    } catch (error) {
      console.error("Error creating account:", error)
      setError("Failed to create account. Please try again.")
      setLoading(false)
    }
  }

  const handleButtonClick = async () => {
    await submitForm()
  }

  const doesEmailExist = async (email: string) => {
    try {
      const data = await UserExists.verifyUserExists({
        attributes_to_verify: {
          email,
        },
      })

      return { found: data.user_found }
    } catch (error) {
      console.error(error)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    setIsTemp(1)

    const emailSchema = z.string().email().safeParse(value)
    if (!emailSchema.success) {
      return
    }
  }

  const handleNameChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: "first" | "last"
  ) => {
    const value = e.target.value
    if (field === "first") {
      setFirstName(value)
    } else {
      setLastName(value)
    }
  }

  const getConsultationAvailabilityId = () => {
    let activeAvailabilityIdArr: Array<string> = []
    const activeConsultationAvailability = userAvailabilities.find(
      (availability) => {
        return (
          availability.id === service?.service_details?.availability_rule_id
        )
      }
    )
    const defaultAvailability = userAvailabilities.find(
      (availability) => availability.is_default
    )
    const firstAvailability = userAvailabilities[0]
    if (activeConsultationAvailability?.id) {
      activeAvailabilityIdArr.push(activeConsultationAvailability.id.toString())
    } else if (defaultAvailability?.id) {
      activeAvailabilityIdArr.push(defaultAvailability.id.toString())
    } else if (firstAvailability?.id) {
      activeAvailabilityIdArr.push(firstAvailability.id.toString())
    }
    return activeAvailabilityIdArr
  }

  const getAvailability = () => {
    setLoadingAvailablePeriods(true)
    const profileIds = [coachPublicProfileData?.coach_profile_id]
    let now = moment.utc()
    let active_date = moment(activeDate)
    let start_date: moment.Moment = active_date

    if (active_date.isSame(now, "day")) {
      // For non-creators on today's date, the booking window pushes the effective date to tomorrow
      // So we should request availability for tomorrow, not today with a time offset
      start_date = now.clone().add(bookingWindow, "hours")
    } else {
      start_date = active_date.clone().startOf("day")
    }
    const finalStartDate = start_date.toISOString()

    const startMoment = moment.utc(finalStartDate)

    const end = startMoment.clone().tz(localTimeZone).endOf("day").toISOString()
    const getAvailabilityRequest: GetAvailability = {
      profile_ids: profileIds,
      creator_profile_id: parseInt(coachPublicProfileData?.coach_profile_id),
      availability_rule_ids: getConsultationAvailabilityId(),
      meeting_duration: duration || durationDefault,
      start_date: finalStartDate,
      end_date: end,
      coach_user_id: parseInt(coachPublicProfileData?.user_id),
      show_all_times: true,
    }
    CalendarRequests.getAvailability(getAvailabilityRequest)
      .then((data) => {
        setAvailablePeriods(data.available_periods)
        setLoadingAvailablePeriods(false)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
        setLoadingAvailablePeriods(false)
      })
  }

  const getAvailabilityRules = async () => {
    setRulesLoading(true)
    try {
      const data = await CalendarRequests.getAvailabilityRules({
        user_id: parseInt(coachPublicProfileData?.user_id),
      })
      setUserAvailabilities(data.availability_rules)
      const matchingAvailability = data.availability_rules.find(
        (rule: any) =>
          rule.id === service?.service_details?.availability_rule_id
      )
      if (matchingAvailability) {
        setBookingWindow(matchingAvailability.min_book_window / 60)
      }
    } catch (ex) {
    } finally {
      setRulesLoading(false)
    }
  }

  const goToSignIn = () => {
    const start = new Date(startTime!)
    const end = new Date(start.getTime() + duration * 60000)

    const meetingToSchedule = {
      startTime: start.toISOString(),
      endTime: end.toISOString(),
    }

    localStorage.setItem("meetingToSchedule", JSON.stringify(meetingToSchedule))

    const coachRedirectPath = `/coach/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
    const memberRedirectPath = `/member/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
    if (user) {
      navigate(
        `/${user.activeProfile}/${coachPublicProfileData?.endpoint_slug}/services/${service?.service_id}`
      )
    } else {
      localStorage.setItem("coachRedirect", coachRedirectPath)
      localStorage.setItem("memberRedirect", memberRedirectPath)
      window.location.href = "/sign-in"
    }
  }

  const canSubmit =
    !!activeDate &&
    !!startTime &&
    (firstName || "").trim().length > 0 &&
    (lastName || "").trim().length > 0 &&
    (email || "").trim().length > 0 &&
    !!isNotRobot

  const getUserTimezoneFullNameAndAbbr = () => {
    const date = new Date()
    const parts = date
      .toLocaleDateString("en-US", {
        timeZoneName: "long",
      })
      .split(", ")

    const fullName = parts[1] || ""

    const abbr =
      date
        .toLocaleTimeString("en-US", {
          timeZoneName: "short",
        })
        .split(" ")
        .pop() || ""

    return `${fullName} (${abbr})`
  }

  const userTimezoneFull = getUserTimezoneFullNameAndAbbr()

  useEffect(() => {
    if (
      service?.service_details?.type === "consultation" &&
      !rulesLoading &&
      userAvailabilities.length > 0
    ) {
      getAvailability()
    }
  }, [activeDate, userAvailabilities, duration, rulesLoading])

  useEffect(() => {
    if (String(coachPublicProfileData?.user_id) === user?.user_id) {
      setIsOwner(true)
    } else {
      setIsOwner(false)
    }
  }, [coachPublicProfileData, user])

  useEffect(() => {
    const tempPassword = generatePassword()
    setPassword(tempPassword)
  }, [setPassword])

  useEffect(() => {
    if (service && coachPublicProfileData) {
      getProfileServiceList(coachPublicProfileData)
    }
    if (service?.service_details?.type === "consultation") {
      getAvailabilityRules().then(() => getAvailability())
    }
  }, [service, coachPublicProfileData])

  useEffect(() => {
    if (userAvailabilities && duration && activeDate) {
      getAvailability()
    }
  }, [activeDate, userAvailabilities, duration])

  useEffect(() => {
    const state = location.state as BookingState | undefined
    if (state) {
      setPrepopulatedStartTime(state?.start)
      setPrepopulatedEndTime(state?.end)
    }
  }, [location.state, navigate])

  useEffect(() => {
    if (startTime && duration) {
      const startTimeUTC = startTime.toISOString()
      const endTime = new Date(startTime.getTime() + duration * 60000)
      const endTimeUTC = endTime.toISOString()

      setUTCStartTime(startTimeUTC)
      setUTCEndTime(endTimeUTC)
    }
  }, [startTime, duration])

  useEffect(() => {
    if (prepopulatedStartTime) {
      setActiveDate(prepopulatedStartTime)
    }
  }, [prepopulatedStartTime])

  return (
    <div className="flex flex-col items-center w-full mb-4">
      {" "}
      <div className="gap-4 md:gap-20 mb-4 w-full">
        <div className="w-full flex flex-col lg:flex-row items-start gap-4 md:gap-20 mt-12 mb-12">
          <div
            ref={leftContainerRef}
            className="w-full flex flex-col justify-center items-center px-[20px]"
          >
            <CustomDayPicker
              setActiveDate={setActiveDate}
              disablePastDates={true}
              dayColor={coachColor}
              bookingWindow={bookingWindow}
              prepopulatedDate={prepopulatedStartTime}
            />
            <p className="mt-8 text-lg font-bold">
              Times are displayed in {userTimezoneFull}
            </p>
          </div>

          <div
            className="flex w-full flex-col p-2 gap-[8px] overflow-y-auto"
            style={{
              maxHeight: leftHeight ? `${leftHeight}px` : "auto",
            }}
          >
            {loadingAvailablePeriods ? (
              <div className="px-[8px] pt-[24px] text-center font-bold">
                <Loader />
                <h3 className="text-center text-[16px] font-bold">
                  Loading available meeting times.
                </h3>
              </div>
            ) : (
              <>
                {availablePeriods?.length > 0 ? (
                  <>
                    {availablePeriods.map((availablePeriod, idx) => (
                      <Fragment key={idx}>
                        <TimeOptions
                          availabilityStart={moment(
                            availablePeriod.start
                          ).toDate()}
                          availabilityEnd={moment(availablePeriod.end).toDate()}
                          duration={duration || durationDefault}
                          setStartTime={setStartTime}
                          startTime={startTime}
                          dayColor={coachColor}
                          prepopulatedStartTime={prepopulatedStartTime}
                          bookingWindow={bookingWindow}
                        />
                        <div className="bg-hover text-graySlate flex items-center justify-center rounded-[10px] p-[12px] text-base font-bold last:hidden">
                          Unavailable
                        </div>
                      </Fragment>
                    ))}
                  </>
                ) : (
                  <div className="mb-2 px-[4px] pt-[5px] text-center text-sm font-bold">
                    No available meeting times for you and your meeting
                    participant(s) on this day.
                  </div>
                )}
              </>
            )}
          </div>
        </div>{" "}
        <div className="bg-grayMist mb-8 h-[1px] w-full px-[20px]"></div>
        <div className="flex flex-col gap-[12px] px-[20px] md:col-span-2 justify-center items-center">
          {window.location.href.includes("booking") && (
            <div className="text-lg font-medium">
              <span
                className="text-blurple font-bold hover:underline cursor-pointer"
                onClick={goToSignIn}
              >
                Sign in
              </span>{" "}
              or <span className="font-bold">continue as guest:</span>
            </div>
          )}

          <div className="flex flex-col w-full md:w-3/4">
            <Input
              label="Email"
              id="email"
              placeholder="Email Address"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              style={{
                backgroundColor: errors.email ? "rgba(255, 0, 0, 0.05)" : "",
              }}
            />
            {errors.email && (
              <div className="flex items-center text-red-500 text-sm mt-1">
                <SvgWarning className="w-[18px] h-[18px] mr-1" />
                {errors.email}
              </div>
            )}
          </div>

          <div className="flex gap-4 w-full md:w-3/4">
            <div className="flex flex-col w-full">
              <Input
                label="First name"
                id="first-name"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => handleNameChange(e, "first")}
                disabled={loading}
                className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              />
            </div>
            <div className="flex flex-col w-full">
              <Input
                label="Last name"
                id="last-name"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => handleNameChange(e, "last")}
                disabled={loading}
                className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-4 w-1/2 sm:w-1/3">
            <Recaptcha setIsNotRobot={setIsNotRobot} />

            <Button
              onClick={handleButtonClick}
              disabled={!canSubmit || loading}
              variant="rounded"
              className="w-full mt-4"
            >
              {loading ? <Loader /> : "Book"}
            </Button>
          </div>
          <div>
            <p className="text-muted-foreground text-xs text-pretty w-full mx-auto">
              By continuing, I agree to Zoee's{" "}
              <Link
                to="/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blurple"
              >
                Terms
              </Link>{" "}
              &{" "}
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blurple"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
      {showSuccessPopup && startTime && (
        <ConsultationSuccessPopup
          startTime={startTime}
          coachPublicProfileData={coachPublicProfileData}
          durationInSeconds={Number(service.service_details.session.duration)}
        />
      )}{" "}
    </div>
  )
}

export default FreeConsultGuestCreation
